import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import Swal from "sweetalert2";
import React from "react";

import { zodResolver } from "../../../utils/zodResolver";
import { LoadingButton } from "@mui/lab";
import { RuleFormSchema, type RuleFormSchemaType } from "../ruleForm.schema";

import {
  useCreateRuleMutation,
  useGetDistinctMaterialCategoriesQuery,
} from "../../../api/services/ruleApi";
import { useLazyGetAllPlantsDetailsQuery } from "../../../api/services/plantApi";

const RuleForm: React.FC = () => {
  const navigate = useNavigate();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<RuleFormSchemaType>({
    resolver: zodResolver(RuleFormSchema),
    defaultValues: {
      plant: "",
      workCenter: "",
      materialCategory: "",
      approvalLevel: "",
    },
  });

  const staticMaterialCategories = ["A", "B", "C"];

  const { data: materialCategoriesData } =
    useGetDistinctMaterialCategoriesQuery();
  const materialCategories =
    materialCategoriesData?.materialCategories || staticMaterialCategories;

  const [getPlants, { data: plantsData }] = useLazyGetAllPlantsDetailsQuery();
  React.useEffect(() => {
    getPlants({});
  }, []);
  const plants = plantsData?.plants || [];

  const [createRule, {isLoading}] = useCreateRuleMutation();

  const onSubmit = async (form: RuleFormSchemaType) => {
    try {
      await createRule({
        plantName: form.plant,
        workCenter: form.workCenter,
        materialCategory: form.materialCategory,
        approvalLevel: Number(form.approvalLevel),
      }).unwrap();

      Swal.fire({ icon: "success", title: "Rule created!" });
      navigate("/rule");
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.data?.message || "Rule already exists!",
      });
    }
  };
  return (
    <Box height="100vh">
      <Stack direction="row" alignItems="center" bgcolor="white" px={2} mb={2}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackOutlinedIcon />
        </IconButton>
        <Box p={2.5}>
          <Typography variant="h6">Add New Rule</Typography>
          <Typography fontSize={13} color="text.secondary">
          All spare part requests approved by the manager will appear here for your review and processing.
        </Typography>
        </Box>
      </Stack>

      <Paper
        sx={{
          p: 3,
          maxWidth: 600,
          bgcolor: "background.default",
          boxShadow: "none",
          borderRadius: 3,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Plant */}
          <Typography sx={{ fontSize: 14, fontWeight: 600,color:'rgba(109, 110, 112, 1)' }}>Plant</Typography>
          <Controller
            name="plant"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth margin="normal" error={!!errors.plant}>
                <Select {...field} displayEmpty sx={{ bgcolor: "white" ,color:"rgba(53, 54, 55, 1)"}}>
                <MenuItem value="">Choose Any Plant</MenuItem>
                {plants.map((p: any) => (
                  <MenuItem key={p.name} value={p.name}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="error">
                {errors.plant?.message}
              </Typography>
            </FormControl>
          )}
        />

          {/* Work Center */}
          <Typography sx={{ fontSize: 14, fontWeight: 600,color:'rgba(109, 110, 112, 1)' }}>
            Work Center
          </Typography>
          <Controller
            name="workCenter"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                margin="normal"
                error={!!errors.workCenter}
              >
                <Select {...field} displayEmpty sx={{ bgcolor: "white",color:"rgba(53, 54, 55, 1)"}}>
                  <MenuItem value="">Choose Any Work Center</MenuItem>
                  {["Production", "Utility", "Corrective", "Preventive"].map(
                    (w) => (
                      <MenuItem key={w} value={w}>
                        {w}
                      </MenuItem>
                    ))}
                  </Select>
                    <Typography variant="caption" color="error">
                      {errors.workCenter?.message}
                    </Typography>
              </FormControl>
            )}
          />

          {/* Material Category */}
          <Typography sx={{ fontSize: 14, fontWeight: 600,color:'rgba(109, 110, 112, 1)' }}>
            Material Category
          </Typography>
          <Controller
            name="materialCategory"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                margin="normal"
                error={!!errors.materialCategory}
              >
                <Select {...field} displayEmpty sx={{ bgcolor: "white",color:"rgba(53, 54, 55, 1)" }}>
                  <MenuItem value="">Choose Any Material Category</MenuItem>
                  {materialCategories.map((c: any) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
            <Typography variant="caption" color="error">
              {errors.materialCategory?.message}
            </Typography>
              </FormControl>
            )}
          />

          {/* Approval Level */}
          <Typography sx={{ fontSize: 14, fontWeight: 600,color:'rgba(109, 110, 112, 1)' }}>
            Approval Level
          </Typography>
          <Controller
            name="approvalLevel"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                margin="normal"
                error={!!errors.approvalLevel}
              >
                <Select {...field} displayEmpty sx={{ bgcolor: "white",color:"rgba(53, 54, 55, 1)" }}>
                  <MenuItem value="">Select Approval Level</MenuItem>
                  <MenuItem value="1">Level 1</MenuItem>
                  <MenuItem value="2">Level 2</MenuItem>
                  <MenuItem value="3">Level 3</MenuItem>
                </Select>
                <Typography variant="caption" color="error">
                  {errors.approvalLevel?.message}
                </Typography>
              </FormControl>
            )}
          />

          {/* Buttons */}
          <Stack direction="row" justifyContent="space-between" mt={4}>
            <Button variant="outlined" onClick={() => reset()} sx={{border:'none',backgroundColor:'rgba(0, 32, 96, 0.1)',color:'rgba(0, 32, 96, 1)  ',textTransform: "none"}}>
              Cancel
            </Button>
            <LoadingButton type="submit" variant="contained" loading={isLoading} sx={{textTransform: "none"}}>
              Create Rule
            </LoadingButton>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default RuleForm;
